import {
  useEffect,
  useRef,
  useCallback,
  useImperativeHandle,
  forwardRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { createChart, CandlestickSeries, IChartApi, ISeriesApi, CandlestickData, Time } from 'lightweight-charts';
import type { Candle, ChartMarker, MethodologyOverlay } from '@trading/shared';
import { isCorruptCandle } from '@trading/shared';
import ChartNeonOverlay from './ChartNeonOverlay';
import ChartDrawingToolbar from './ChartDrawingToolbar';
import { useChartDrawing } from './useChartDrawing';
import ChartScanAnimation from './ChartScanAnimation';
import { formatUtcTime, computeTickCandle } from './chartUtils';

interface Tick {
  symbol: string;
  price: number;
  timestamp: number;
}

/**
 * Maximum number of ticks held in the pre-history buffer.
 * Ticks arriving before `setInitialCandles` is called are buffered here
 * and replayed once history is available. When the limit is reached the
 * oldest tick is evicted (FIFO) to keep memory usage bounded.
 */
export const TICK_BUFFER_MAX = 64;

/** Default number of most-recent candles to show on initial load.
 *  Tuned to roughly match TradingView's default zoom (~4–5 months on the
 *  daily timeframe). Older data still loads in the background and pans into view. */
export const DEFAULT_VISIBLE_CANDLES = 120;

interface TradeSetup {
  direction: 'LONG' | 'SHORT' | null;
  entry?: { price: number } | null;
  stopLoss?: { price: number } | null;
  targets?: {
    t1?: { price: number };
    t2?: { price: number };
    t3?: { price: number };
  } | null;
}

interface SMCData {
  orderBlocks?: Array<{
    type: 'bullish' | 'bearish';
    top: number;
    bottom: number;
    strength: number;
    status: 'fresh' | 'tested' | 'mitigated';
  }>;
  fvg?: Array<{
    type: 'bullish' | 'bearish';
    top: number;
    bottom: number;
    filledPercent: number;
  }>;
  liquidity?: Array<{
    type: 'bsl' | 'ssl';
    price: number;
    strength: number;
  }>;
  lastBos?: { type: 'bullish' | 'bearish'; price: number; time: number } | null;
  lastChoch?: { type: 'bullish' | 'bearish'; price: number; time: number } | null;
  keyLevels?: {
    recentHigh: number;
    recentLow: number;
    swingHigh: number;
    swingLow: number;
  };
}

export interface CrosshairOHLC {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
}

interface ChartProps {
  markers?: ChartMarker[];
  tradeSetup?: TradeSetup | null;
  /**
   * @deprecated Prefer `methodologyOverlay` (discriminated union). Kept for
   * back-compat with the SMC-only payload — when both props are passed,
   * `methodologyOverlay` wins.
   */
  smcData?: SMCData | null;
  /**
   * v3 method-aware overlay. When provided, drives the chart visualization
   * for the selected methodology (SMC / ICT / Price Action / Fibonacci).
   */
  methodologyOverlay?: MethodologyOverlay | null;
  showSMC?: boolean;
  analysisTime?: number | Date | null;
  /** Sets `minHeight` on the container div. With `autoSize: true` the library
   *  reads the container's computed dimensions via its own ResizeObserver, so
   *  this prop only guarantees a minimum — actual chart height follows the
   *  container's CSS-driven size. */
  height?: number;
  timeframe?: string;
  /** Symbol — scopes saved drawings per instrument. */
  symbol?: string;
  /** Enables the interactive drawing toolbar with per-symbol/timeframe persistence. */
  enableDrawing?: boolean;
  isScanning?: boolean;
  onCrosshairMove?: (data: CrosshairOHLC | null) => void;
  /** Called on every visible logical range change — parent owns low-water-mark logic. */
  onVisibleRangeChange?: (range: { from: number; to: number }) => void;
  /** Parent-driven loading indicator — true while history fetch is in flight. */
  isLoadingHistory?: boolean;
  /** Parent-driven exhaustion flag — when true show the "no more data" badge. */
  isExhausted?: boolean;
}

export interface ChartHandle {
  updateWithTick: (tick: Tick) => void;
  prependCandles: (newCandles: Candle[]) => void;
  fitContent: () => void;
  setInitialCandles: (candles: Candle[], opts?: { preserveVisibleRange?: boolean }) => void;
}

/** Debounce window (ms) for visible-range change events — coalesces wheel bursts into one fetch. */
export const RANGE_CHANGE_DEBOUNCE_MS = 200;

const Chart = forwardRef<ChartHandle, ChartProps>(function Chart(
  {
    markers = [],
    tradeSetup,
    smcData,
    methodologyOverlay,
    showSMC = true,
    analysisTime,
    height,
    timeframe = '5m',
    symbol = '',
    enableDrawing = false,
    isScanning = false,
    onCrosshairMove,
    onVisibleRangeChange,
    isLoadingHistory = false,
    isExhausted = false,
  },
  ref,
) {
  const { t } = useTranslation(['common']);
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const lastCandleRef = useRef<CandlestickData<Time> | null>(null);
  // Dev-only: dedupe drift warnings per (symbol, timeframe, sign of drift) so
  // stale-replay (drift < 0) and forward-clock-skew (drift > 0) each warn once
  // independently — they're different failure classes and conflating them hides
  // one when the other fires first.
  const hasWarnedDriftRef = useRef<Map<string, true>>(new Map());
  /** Ticks that arrived before the first `setInitialCandles` call. Replayed once history is set. */
  const preHistoryTickBuffer = useRef<Tick[]>([]);
  const onVisibleRangeChangeRef = useRef(onVisibleRangeChange);
  const onCrosshairMoveRef = useRef(onCrosshairMove);

  const [chartReady, setChartReady] = useState(false);
  const [showNoMoreData, setShowNoMoreData] = useState(false);
  const noMoreDataTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Pending debounce timer for visible-range change events. Cancelled on unmount. */
  const rangeDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep callback refs updated
  useEffect(() => {
    onVisibleRangeChangeRef.current = onVisibleRangeChange;
  }, [onVisibleRangeChange]);
  useEffect(() => {
    onCrosshairMoveRef.current = onCrosshairMove;
  }, [onCrosshairMove]);

  // Show "no more data" badge when parent signals exhaustion
  useEffect(() => {
    if (!isExhausted) return;
    setShowNoMoreData(true);
    noMoreDataTimerRef.current = setTimeout(() => {
      setShowNoMoreData(false);
      noMoreDataTimerRef.current = null;
    }, 4000);
    return () => {
      if (noMoreDataTimerRef.current !== null) {
        clearTimeout(noMoreDataTimerRef.current);
        noMoreDataTimerRef.current = null;
      }
      // Clear the badge immediately when the symbol changes (isExhausted flips
      // true → false on RESET) so the badge does not persist into the new symbol load.
      setShowNoMoreData(false);
    };
  }, [isExhausted]);

  // Get timeframe in seconds
  const getTimeframeSeconds = useCallback(() => {
    const map: Record<string, number> = {
      '1m': 60,
      '5m': 300,
      '15m': 900,
      '30m': 1800,
      '1h': 3600,
      '4h': 14400,
      '1d': 86400,
    };
    return map[timeframe] || 300;
  }, [timeframe]);

  // Expose methods to parent via ref
  useImperativeHandle(
    ref,
    () => {
      /**
       * Apply a single tick against the live series refs using the pure
       * `computeTickCandle` helper. Both `updateWithTick` (live path) and
       * `setInitialCandles` (replay path) share this function so the
       * candle-math logic has a single source of truth.
       */
      function applyTick(tick: Tick): void {
        if (!seriesRef.current || !lastCandleRef.current) return;
        const result = computeTickCandle(lastCandleRef.current, tick, getTimeframeSeconds());
        if (result === null) return;
        if (isCorruptCandle(result as unknown as Candle)) {
          if (import.meta.env.DEV) console.warn('[Chart] corrupt buffered tick candle dropped', result);
          return;
        }
        seriesRef.current.update(result);
        lastCandleRef.current = result;
      }

      return {
        updateWithTick: (tick: Tick) => {
          if (!Number.isFinite(tick.price) || !Number.isFinite(tick.timestamp)) return;

          // History has not been initialised yet — buffer the tick for replay
          // once setInitialCandles runs, rather than silently dropping it.
          // Caller (Analysis.tsx) filters ticks by symbol before forwarding here,
          // so the buffer only ever contains ticks for the currently active symbol.
          if (!seriesRef.current || !lastCandleRef.current) {
            preHistoryTickBuffer.current.push(tick);
            if (preHistoryTickBuffer.current.length > TICK_BUFFER_MAX) {
              preHistoryTickBuffer.current.shift();
              if (process.env.NODE_ENV !== 'production') {
                console.warn('[Chart] tick buffer overflow — dropping oldest tick');
              }
            }
            return;
          }

          // Live path: use computeTickCandle for the shared candle-math; emit
          // dev-only drift warn when the result is null (stale tick).
          // applyTick is used for buffered replay — the live path calls
          // computeTickCandle directly so it can inspect null and warn on drift.
          const tfSeconds = getTimeframeSeconds();
          const liveResult = computeTickCandle(lastCandleRef.current, tick, tfSeconds);
          if (liveResult !== null) {
            if (!isCorruptCandle(liveResult as unknown as Candle)) {
              seriesRef.current.update(liveResult);
              lastCandleRef.current = liveResult;
            } else if (import.meta.env.DEV) {
              console.warn('[Chart] corrupt live tick candle dropped', liveResult);
            }
          } else if (import.meta.env.DEV) {
            const lastCandleTime = lastCandleRef.current.time as number;
            const tickTime = Math.floor(tick.timestamp / 1000);
            const candleTime = Math.floor(tickTime / tfSeconds) * tfSeconds;
            const drift = candleTime - lastCandleTime;
            const key = `${tick.symbol}:${timeframe}:${Math.sign(drift)}`;
            if (!hasWarnedDriftRef.current.has(key)) {
              hasWarnedDriftRef.current.set(key, true);
              // Observability only — drop behaviour is unchanged. Negative drift
              // = stale tick (e.g. Redis replay on reconnect); positive drift =
              // last-candle clock ahead of tick clock (TZ misparse, server skew).
              console.warn('[Chart] live tick dropped (drift)', {
                symbol: tick.symbol,
                timeframe,
                candleTime,
                lastCandleTime,
                drift,
              });
            }
          }
        },

        setInitialCandles: (newCandles: Candle[], opts?: { preserveVisibleRange?: boolean }) => {
          if (!seriesRef.current || !chartRef.current) return;
          const chartData = newCandles
            .filter(
              (c): c is NonNullable<typeof c> =>
                c != null &&
                Number.isFinite(c.time) &&
                Number.isFinite(c.open) &&
                Number.isFinite(c.high) &&
                Number.isFinite(c.low) &&
                Number.isFinite(c.close),
            )
            .map((c) => ({ time: c.time as Time, open: c.open, high: c.high, low: c.low, close: c.close }));
          if (chartData.length === 0) return;

          // When `preserveVisibleRange` is set (e.g. swapping data providers
          // for the same symbol/timeframe), capture the current visible
          // *time* window before replacing the series. Time-based preservation
          // is more robust than logical-index across providers, since the two
          // feeds may have slightly different bar counts but should share
          // timestamps on the same timeframe.
          const previousVisibleTimeRange = opts?.preserveVisibleRange
            ? chartRef.current.timeScale().getVisibleRange()
            : null;

          seriesRef.current.setData(chartData);

          try {
            if (previousVisibleTimeRange) {
              chartRef.current.timeScale().setVisibleRange(previousVisibleTimeRange);
            } else {
              const visibleCount = Math.min(DEFAULT_VISIBLE_CANDLES, chartData.length);
              chartRef.current.timeScale().setVisibleLogicalRange({
                from: chartData.length - visibleCount,
                to: chartData.length - 1,
              });
            }
          } catch {
            // setVisibleRange/setVisibleLogicalRange can throw on mobile Safari
            // if the chart's internal model is in a transient state after setData
          }
          lastCandleRef.current = chartData[chartData.length - 1];
          // Reset drift-warn dedupe so a fresh load (or symbol/timeframe switch)
          // can warn again if drift recurs against the new baseline.
          hasWarnedDriftRef.current.clear();

          // Replay any ticks that arrived during the history fetch window.
          // Clear the buffer first so a concurrent updateWithTick that fires
          // during the synchronous replay loop goes to applyTick directly.
          const buffered = preHistoryTickBuffer.current;
          preHistoryTickBuffer.current = [];
          for (const tick of buffered) {
            applyTick(tick);
          }
        },

        prependCandles: (newCandles: Candle[]) => {
          if (!seriesRef.current || !chartRef.current) return;

          const currentData = seriesRef.current.data() as CandlestickData<Time>[];

          const newChartData: CandlestickData<Time>[] = newCandles
            .filter(
              (candle): candle is NonNullable<typeof candle> =>
                candle != null &&
                Number.isFinite(candle.time) &&
                Number.isFinite(candle.open) &&
                Number.isFinite(candle.high) &&
                Number.isFinite(candle.low) &&
                Number.isFinite(candle.close),
            )
            .map((candle) => ({
              time: candle.time as Time,
              open: candle.open,
              high: candle.high,
              low: candle.low,
              close: candle.close,
            }));

          const existingTimes = new Set(currentData.map((c) => c.time));
          const uniqueNewData = newChartData.filter((c) => !existingTimes.has(c.time));

          if (uniqueNewData.length === 0) return;

          const allData = [...uniqueNewData, ...currentData].sort(
            (a, b) => (a.time as number) - (b.time as number),
          );

          // Capture the user's current visible range BEFORE setData. Otherwise
          // setData snaps the chart to the right edge and the freshly
          // prepended candles are invisible until the user pans left manually.
          const visibleLogicalBefore = chartRef.current.timeScale().getVisibleLogicalRange();

          seriesRef.current.setData(allData);

          // Shift the visible range right by `uniqueNewData.length` so the user
          // continues to see the same bars they were looking at — the prepended
          // older candles sit to their left, ready to scroll into.
          if (visibleLogicalBefore) {
            chartRef.current.timeScale().setVisibleLogicalRange({
              from: visibleLogicalBefore.from + uniqueNewData.length,
              to: visibleLogicalBefore.to + uniqueNewData.length,
            });
          }
        },

        fitContent: () => {
          chartRef.current?.timeScale().fitContent();
        },
      };
    },
    [getTimeframeSeconds, timeframe],
  );

  // Create chart on mount
  useEffect(() => {
    if (!containerRef.current || chartRef.current) return;

    const chart = createChart(containerRef.current, {
      autoSize: true,
      layout: {
        background: { color: 'transparent' },
        textColor: '#94a3b8',
        fontFamily: 'Inter, sans-serif',
        // Hide the in-chart TradingView logo. The Apache-2 licence only requires
        // a link to tradingview.com somewhere on the site — satisfied by the
        // "Charts by TradingView" footer link on the landing page (PR #843).
        attributionLogo: false,
      },
      grid: {
        vertLines: { color: '#1e293b' },
        horzLines: { color: '#1e293b' },
      },
      crosshair: {
        mode: 0,
        vertLine: {
          color: '#475569',
          width: 1,
          style: 2,
          labelBackgroundColor: '#3b82f6',
        },
        horzLine: {
          color: '#475569',
          width: 1,
          style: 2,
          labelBackgroundColor: '#3b82f6',
        },
      },
      rightPriceScale: {
        visible: true,
        borderColor: '#1e293b',
        scaleMargins: { top: 0.1, bottom: 0.1 },
        autoScale: true,
        borderVisible: true,
        entireTextOnly: false,
        ticksVisible: true,
      },
      localization: {
        priceFormatter: (price: number) => price.toFixed(5),
        timeFormatter: (time: number) => formatUtcTime(time),
      },
      timeScale: {
        borderColor: '#1e293b',
        timeVisible: true,
        secondsVisible: false,
      },
    });

    const series = chart.addSeries(CandlestickSeries, {
      upColor: '#22c55e',
      downColor: '#ef4444',
      borderUpColor: '#22c55e',
      borderDownColor: '#ef4444',
      wickUpColor: '#22c55e',
      wickDownColor: '#ef4444',
      priceFormat: {
        type: 'price',
        precision: 5,
        minMove: 0.00001,
      },
    });

    chartRef.current = chart;
    seriesRef.current = series;
    setChartReady(true);

    // Build OHLC lookup from series data for crosshair
    const getOHLCForTime = (timeVal: number): CrosshairOHLC | null => {
      const data = series.data() as CandlestickData<Time>[];
      let lo = 0;
      let hi = data.length - 1;
      while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        const t = data[mid].time as number;
        if (t === timeVal) {
          return {
            time: timeVal,
            open: data[mid].open,
            high: data[mid].high,
            low: data[mid].low,
            close: data[mid].close,
          };
        } else if (t < timeVal) {
          lo = mid + 1;
        } else {
          hi = mid - 1;
        }
      }
      return null;
    };

    chart.subscribeCrosshairMove((param) => {
      if (!onCrosshairMoveRef.current) return;
      if (!param.time || !param.point) {
        onCrosshairMoveRef.current(null);
        return;
      }
      const ohlc = getOHLCForTime(param.time as number);
      if (ohlc) {
        onCrosshairMoveRef.current(ohlc);
      } else {
        const price = series.coordinateToPrice(param.point.y) ?? 0;
        onCrosshairMoveRef.current({
          time: param.time as number,
          open: price,
          high: price,
          low: price,
          close: price,
        });
      }
    });

    // Emit visible range changes to parent — debounced to 200 ms so a single
    // wheel gesture only triggers one candle fetch instead of 10–30 requests.
    chart.timeScale().subscribeVisibleLogicalRangeChange((logicalRange) => {
      if (!logicalRange || !onVisibleRangeChangeRef.current) return;
      if (rangeDebounceRef.current !== null) {
        clearTimeout(rangeDebounceRef.current);
      }
      rangeDebounceRef.current = setTimeout(() => {
        rangeDebounceRef.current = null;
        onVisibleRangeChangeRef.current?.({ from: logicalRange.from, to: logicalRange.to });
      }, RANGE_CHANGE_DEBOUNCE_MS);
    });

    return () => {
      if (rangeDebounceRef.current !== null) {
        clearTimeout(rangeDebounceRef.current);
        rangeDebounceRef.current = null;
      }
      if (noMoreDataTimerRef.current !== null) {
        clearTimeout(noMoreDataTimerRef.current);
        noMoreDataTimerRef.current = null;
      }
      setChartReady(false);
      chartRef.current = null;
      chart.remove();
    };
  }, []);

  // Add markers for analysis results
  useEffect(() => {
    if (!seriesRef.current || !chartRef.current || markers.length === 0) return;

    markers.forEach((marker) => {
      if (marker.type === 'line' && marker.coordinates.price) {
        const color =
          marker.color === 'profit'
            ? '#22c55e'
            : marker.color === 'loss'
              ? '#ef4444'
              : marker.color === 'accent'
                ? '#3b82f6'
                : '#64748b';

        seriesRef.current!.createPriceLine({
          price: marker.coordinates.price,
          color,
          lineWidth: 1,
          lineStyle: 2,
          axisLabelVisible: true,
          title: marker.label,
        });
      }
    });

    return () => {
      // price lines cleaned up on series destroy
    };
  }, [markers]);

  const neonLevels: Array<{
    price: number;
    type: 'entry' | 'stopLoss' | 't1' | 't2' | 't3';
    label: string;
  }> = [];

  // Calculate price range from current series data for trade setup validation
  const currentSeriesData = seriesRef.current?.data() as CandlestickData<Time>[] | undefined;
  const candlePriceRange =
    currentSeriesData && currentSeriesData.length > 0
      ? {
          min: Math.min(...currentSeriesData.map((c) => c.low)),
          max: Math.max(...currentSeriesData.map((c) => c.high)),
        }
      : null;

  const isPriceInRange = (price: number): boolean => {
    if (!candlePriceRange) return true;
    const range = candlePriceRange.max - candlePriceRange.min;
    const avgPrice = (candlePriceRange.max + candlePriceRange.min) / 2;
    const minMargin = avgPrice * 0.02;
    const margin = Math.max(range * 0.5, minMargin);
    return price >= candlePriceRange.min - margin && price <= candlePriceRange.max + margin;
  };

  const entryPrice = tradeSetup?.entry?.price;
  const isSetupValid = entryPrice ? isPriceInRange(entryPrice) : true;

  if (isSetupValid) {
    if (tradeSetup?.entry?.price) {
      neonLevels.push({
        price: tradeSetup.entry.price,
        type: 'entry',
        label: tradeSetup.direction === 'LONG' ? '▲ LONG' : '▼ SHORT',
      });
    }
    if (tradeSetup?.stopLoss?.price) {
      neonLevels.push({ price: tradeSetup.stopLoss.price, type: 'stopLoss', label: 'STOP' });
    }
    if (tradeSetup?.targets?.t1?.price) {
      neonLevels.push({ price: tradeSetup.targets.t1.price, type: 't1', label: 'T1' });
    }
    if (tradeSetup?.targets?.t2?.price) {
      neonLevels.push({ price: tradeSetup.targets.t2.price, type: 't2', label: 'T2' });
    }
    if (tradeSetup?.targets?.t3?.price) {
      neonLevels.push({ price: tradeSetup.targets.t3.price, type: 't3', label: 'T3' });
    }
  }

  const drawing = useChartDrawing({
    chartRef,
    seriesRef,
    ready: chartReady,
    enabled: enableDrawing,
    symbol,
    timeframe,
  });

  return (
    <div
      ref={containerRef}
      className="w-full h-full relative"
      style={height ? { minHeight: height } : undefined}
    >
      {enableDrawing && <ChartDrawingToolbar drawing={drawing} />}

      {/* UTC timezone label */}
      <div className="absolute bottom-7 left-2 z-10 pointer-events-none">
        <span className="text-[10px] text-text-muted/60 font-mono select-none">
          {t('chart.timezoneLabel')}
        </span>
      </div>

      {/* Loading history indicator — top-left, top-12 row */}
      {isLoadingHistory && (
        <div className="absolute top-12 left-4 z-20 pointer-events-none">
          <div className="px-3 py-1.5 rounded-md bg-bg-elevated border border-border-subtle shadow-sm flex items-center gap-2">
            <div className="w-3 h-3 rounded-full border border-border-subtle border-t-accent-primary animate-spin" />
            <span className="text-xs text-text-secondary">{t('chart.loadingHistory')}</span>
          </div>
        </div>
      )}

      {/* No more data indicator — centred, top-20 so it doesn't overlap the loading badge */}
      {showNoMoreData && (
        <div className="absolute top-20 left-1/2 -translate-x-1/2 z-20 pointer-events-none">
          <div className="px-3 py-1.5 rounded-md bg-bg-elevated border border-border-subtle shadow-sm">
            <span className="text-xs text-text-secondary">{t('chart.noMoreData')}</span>
          </div>
        </div>
      )}

      {chartReady && (
        <>
          <ChartNeonOverlay
            chart={chartRef.current}
            series={seriesRef.current}
            levels={neonLevels}
            direction={tradeSetup?.direction || null}
            isActive={
              (neonLevels.length > 0 && isSetupValid) ||
              (showSMC && (!!smcData || !!methodologyOverlay) && isSetupValid)
            }
            smcData={isSetupValid ? smcData || undefined : undefined}
            methodologyOverlay={isSetupValid ? (methodologyOverlay ?? null) : null}
            showSMC={showSMC && isSetupValid}
            analysisTime={analysisTime}
          />
          <ChartScanAnimation
            chart={chartRef.current}
            series={seriesRef.current}
            isActive={isScanning}
            duration={5000}
          />
        </>
      )}
    </div>
  );
});

export default Chart;
